import React, { useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView, hoverTooltip, keymap } from '@codemirror/view';
import { EditorSelection } from '@codemirror/state';
import { undo, redo, indentMore, indentLess } from '@codemirror/commands';
import { autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

const EDITOR_FONT =
  '"JetBrains Mono", Monaco, Menlo, "IBM Plex Mono", ui-monospace, Consolas, monospace';

const robinhoodHighlight = HighlightStyle.define([
  { tag: t.keyword, color: '#FF5000' },
  { tag: t.string, color: '#00C805' },
  { tag: t.comment, color: '#6B6B6B', fontStyle: 'italic' },
  { tag: t.number, color: '#F5C344' },
  { tag: t.bool, color: '#F5C344' },
  { tag: t.function(t.variableName), color: '#FFFFFF' },
  { tag: t.definition(t.variableName), color: '#E8E8E8' },
  { tag: t.variableName, color: '#F5F5F5' },
  { tag: t.propertyName, color: '#C8C8C8' },
  { tag: t.operator, color: '#8C8C8C' },
  { tag: t.punctuation, color: '#8C8C8C' },
  { tag: t.className, color: '#00C805' },
]);

const robinhoodCm = EditorView.theme(
  {
    '&': { backgroundColor: '#000000', color: '#F5F5F5' },
    '.cm-gutters': { backgroundColor: '#0A0A0A', color: '#5C5C5C', border: 'none' },
    '.cm-activeLineGutter': { backgroundColor: '#111111', color: '#00C805' },
    '.cm-activeLine': { backgroundColor: 'rgba(0,200,5,0.06)' },
    '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#00C805' },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': {
      backgroundColor: 'rgba(0,200,5,0.22)',
    },
    '.cm-selectionMatch': { backgroundColor: 'rgba(245,195,68,0.18)' },
    '.cm-matchingBracket': { outline: '1px solid #00C805' },
  },
  { dark: true }
);

const ALGO_APIS = {
  alpaca: {
    getAccount: 'Promise — paper/live account (cash, equity, buying_power)',
    getBarsV2: "(symbols, { timeframe, limit, start, end }) → bars by symbol",
    getLatestTrade: '(symbol) → last trade (Price / p / price)',
    getLatestQuote: '(symbol) → bid/ask quote',
    getSnapshot: '(symbol) → snapshot',
    getPosition: '(symbol) → open position or throws',
    getAllPositions: '() → positions[]',
    closePosition: '(symbol) → flatten that symbol',
    closeAllPositions: '() → flatten all',
    createOrder: '(order) → submit order (qty, side, type, time_in_force…)',
    getAllOrders: '(options?) → orders[]',
    cancelOrder: '(orderId)',
    cancelAllOrders: '()',
  },
  helpers: {
    symbol: 'Primary ticker from the IDE Symbols field',
    symbols: 'string[] from the IDE Symbols field',
    symbolY: 'First ticker (pairs Y leg)',
    symbolX: 'Second ticker (pairs X leg)',
    tradeOnlyWhenOpen: 'boolean — from the market-open setting',
    manageExistingPositions: 'boolean',
    venue: 'alpaca | schwab',
    accountNumber: 'Schwab account or null',
    env: 'paper | live',
    schedule: 'normalized run schedule',
    state: 'object — survives each interval tick until Stop',
    ensureTradingSession: '(clock?) → false if closed and setting is on',
    isInSchedule: '() → { ok, reason, clock }',
    getClock: '() → { is_open, isOpen, next_open, next_close }',
    assertMarketOpen: '() — throws only if tradeOnlyWhenOpen and closed',
    getBars: '(symbol, { timeframe, limit }) → bar[]',
    getLatestBar: '(symbol, { timeframe }) → last bar',
    getAccount: '() → same as alpaca.getAccount()',
    getLatestTrade: '(symbol)',
    getLatestQuote: '(symbol)',
    getEarningsCalendar: '({ day|start|end|days })',
    getNews: '(symbol, { limit }) → headlines[]',
    ollamaModel: 'string — OLLAMA_MODEL default',
    askModel: '({ system, user, model }) → { ok, parsed: { action, symbol, confidence, reason } }',
    sizeByRiskPct: '(pct, price) → qty',
    createBracketOrder: '({ symbol, qty, side, takeProfitPct, stopLossPct… })',
    createTrailingStop: '({ symbol, qty, trailPercent })',
    createAlgoOrder: 'Elite VWAP/TWAP payload',
    startTwap: '(params)',
    stopTwap: '(id)',
    emitSignal: '({ kind, label, symbol, price }) — chart marker',
    getAlgoOwnedQty: '(symbol)',
    isAlgoOwned: '(symbol)',
    getAlgoOwnedPositions: '()',
    adoptExistingPositions: '(symbols?)',
  },
  console: {
    log: '(...args) — shown in Output',
    warn: '(...args)',
    error: '(...args)',
  },
};

function objectCompletions(obj, from) {
  return Object.entries(ALGO_APIS[obj] || {}).map(([name, detail]) => ({
    label: name,
    type: detail.startsWith('boolean') || detail.startsWith('object') || detail.includes('|')
      ? 'property'
      : 'function',
    detail: obj,
    info: detail,
    apply: detail.startsWith('(') || detail.includes('→') || detail.startsWith('Promise')
      ? `${name}(`
      : name,
    boost: 99,
  })).map((c) => ({ ...c, from }));
}

function algoCompletions(context) {
  const dotted = context.matchBefore(/[A-Za-z_$][\w$]*\.\w*/);
  if (dotted) {
    const [obj] = dotted.text.split('.');
    if (ALGO_APIS[obj]) {
      const from = dotted.from + obj.length + 1;
      return { from, options: objectCompletions(obj, from), validFor: /^\w*$/ };
    }
  }

  const word = context.matchBefore(/[A-Za-z_$][\w$]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  const globals = [
    { label: 'symbol', type: 'variable', detail: 'IDE Symbols', info: 'Primary ticker from the toolbar Symbols field' },
    { label: 'symbols', type: 'variable', detail: 'IDE Symbols', info: 'string[] of all tickers from the toolbar' },
    { label: 'symbolY', type: 'variable', detail: 'IDE Symbols', info: 'First ticker (pairs dependent leg)' },
    { label: 'symbolX', type: 'variable', detail: 'IDE Symbols', info: 'Second ticker (pairs hedge leg)' },
    { label: 'alpaca', type: 'variable', detail: 'venue client', info: 'Orders, account, bars' },
    { label: 'helpers', type: 'variable', detail: 'desk helpers', info: 'Clock, bars, brackets, TWAP; helpers.symbol mirrors the IDE field' },
    { label: 'console', type: 'variable', detail: 'logs', info: 'log / warn / error → Output' },
    { label: 'runTradingBot', type: 'function', info: 'Typical entry — call at the bottom' },
  ];

  return {
    from: word.from,
    options: globals,
    validFor: /^[\w$]*$/,
  };
}

function algoHover() {
  return hoverTooltip((view, pos) => {
    const { from, to, text } = view.state.wordAt(pos) || {};
    if (from == null) return null;
    const before = view.state.sliceDoc(Math.max(0, from - 24), from);
    const objMatch = before.match(/([A-Za-z_$][\w$]*)\.$/);
    const obj = objMatch?.[1];
    const info = obj && ALGO_APIS[obj] ? ALGO_APIS[obj][text] : null;
    const globalInfo =
      !info && ALGO_APIS[text]
        ? `${text} runtime global`
        : null;
    const body = info || globalInfo;
    if (!body) return null;
    return {
      pos: from,
      end: to,
      create() {
        const dom = document.createElement('div');
        dom.className = 'algo-cm-hover';
        dom.textContent = obj ? `${obj}.${text} — ${body}` : body;
        return { dom };
      },
    };
  });
}

/**
 * CodeMirror JS editor. Completions for alpaca / helpers / console.
 * Monaco was dropped because Tailwind line-height/box-sizing offset the caret.
 */
const AlgoCodeEditor = forwardRef(function AlgoCodeEditor(
  {
    value,
    onChange,
    dark = false,
    palette,
    fontSize = 13,
    wordWrap = false,
    height = 520,
  },
  ref
) {
  const viewRef = useRef(null);
  const scheme = palette || (dark ? 'dark' : 'light');
  const isDark = scheme !== 'light';
  const isHood = scheme === 'robinhood';

  useImperativeHandle(ref, () => ({
    focus: () => viewRef.current?.focus(),
    layout: () => viewRef.current?.requestMeasure(),
    undo: () => {
      const view = viewRef.current;
      if (view) undo(view);
    },
    redo: () => {
      const view = viewRef.current;
      if (view) redo(view);
    },
    find: () => {
      const view = viewRef.current;
      if (!view) return;
      const selected = view.state.sliceDoc(
        view.state.selection.main.from,
        view.state.selection.main.to
      );
      const q = window.prompt('Find', selected || '');
      if (q == null || q === '') return;
      const text = view.state.doc.toString();
      const from = view.state.selection.main.to;
      let idx = text.indexOf(q, from);
      if (idx < 0) idx = text.indexOf(q, 0);
      if (idx < 0) return;
      view.dispatch({
        selection: EditorSelection.range(idx, idx + q.length),
        scrollIntoView: true,
      });
      view.focus();
    },
    replace: () => {
      const view = viewRef.current;
      if (!view) return;
      const find = window.prompt('Find');
      if (find == null || find === '') return;
      const replacement = window.prompt('Replace with', '');
      if (replacement == null) return;
      const text = view.state.doc.toString();
      if (!text.includes(find)) return;
      view.dispatch({
        changes: { from: 0, to: text.length, insert: text.split(find).join(replacement) },
      });
      view.focus();
    },
    format: () => {
      const view = viewRef.current;
      if (!view) return;
      const raw = view.state.doc.toString();
      const formatted = raw
        .split('\n')
        .map((line) => line.replace(/\t/g, '  ').replace(/[ \t]+$/g, ''))
        .join('\n');
      if (formatted !== raw) {
        view.dispatch({
          changes: { from: 0, to: raw.length, insert: formatted },
        });
      }
      view.focus();
    },
    indentMore: () => {
      const view = viewRef.current;
      if (view) indentMore(view);
    },
    indentLess: () => {
      const view = viewRef.current;
      if (view) indentLess(view);
    },
    gotoLine: (line, column = 1) => {
      const view = viewRef.current;
      if (!view) return;
      const doc = view.state.doc;
      const ln = Math.max(1, Math.min(Number(line) || 1, doc.lines));
      const lineObj = doc.line(ln);
      const col = Math.max(1, Number(column) || 1);
      const pos = Math.min(lineObj.from + col - 1, lineObj.to);
      view.dispatch({
        selection: EditorSelection.cursor(pos),
        scrollIntoView: true,
      });
      view.focus();
    },
  }));

  const extensions = useMemo(() => {
    const exts = [
      javascript(),
      autocompletion({
        override: [algoCompletions],
        defaultKeymap: true,
        activateOnTyping: true,
      }),
      keymap.of(completionKeymap),
      algoHover(),
      EditorView.theme(
        {
          '&': {
            height: '100%',
            maxHeight: '100%',
            fontSize: `${fontSize}px`,
            border: 'none',
          },
          '&.cm-focused': { outline: 'none' },
          '.cm-scroller': {
            overflowX: 'auto',
            overflowY: 'scroll',
            fontFamily: EDITOR_FONT,
            lineHeight: '1.55',
            fontVariantLigatures: 'contextual',
          },
          '.cm-content': {
            paddingTop: '8px',
            paddingBottom: '32px',
            caretColor: isHood ? '#00C805' : isDark ? '#aeafad' : '#111827',
            fontFamily: EDITOR_FONT,
            lineHeight: '1.55',
            fontVariantLigatures: 'contextual',
          },
          '.cm-gutters': {
            border: 'none',
            backgroundColor: isHood ? '#0A0A0A' : isDark ? '#252526' : '#f3f4f6',
            color: isHood ? '#5C5C5C' : isDark ? '#6b7280' : '#9ca3af',
          },
          '.cm-activeLineGutter': {
            backgroundColor: isHood ? '#111111' : isDark ? '#2a2d2e' : '#e5e7eb',
          },
          '.cm-activeLine': {
            backgroundColor: isHood ? 'rgba(0,200,5,0.06)' : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          },
          '.cm-tooltip': {
            border: isHood ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            background: isHood ? '#111111' : isDark ? '#1e1e1e' : '#fff',
            color: isHood ? '#F5F5F5' : isDark ? '#e5e7eb' : '#111827',
            fontSize: '12px',
          },
        },
        { dark: isDark }
      ),
    ];
    if (wordWrap) exts.push(EditorView.lineWrapping);
    if (isHood) {
      exts.push(robinhoodCm, syntaxHighlighting(robinhoodHighlight));
    } else if (isDark) {
      exts.push(oneDark);
    }
    return exts;
  }, [isDark, isHood, fontSize, wordWrap]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.requestMeasure();
    const id = window.setTimeout(() => view.requestMeasure(), 0);
    return () => window.clearTimeout(id);
  }, [fontSize, height, wordWrap, scheme, value]);

  const heightCss = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className="algo-cm-host"
      style={{
        height: heightCss,
        maxHeight: heightCss,
        width: '100%',
        overflow: 'hidden',
        background: isHood ? '#000000' : isDark ? '#1e1e1e' : '#ffffff',
      }}
    >
      <CodeMirror
        value={value}
        height="100%"
        width="100%"
        theme={isDark ? 'dark' : 'light'}
        extensions={extensions}
        onChange={(next) => onChange?.(next)}
        onCreateEditor={(view) => {
          viewRef.current = view;
          view.requestMeasure();
        }}
        style={{ height: '100%', width: '100%' }}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false,
          rectangularSelection: true,
          crosshairCursor: false,
          indentOnInput: true,
          tabSize: 2,
          searchKeymap: true,
        }}
      />
    </div>
  );
});

export default AlgoCodeEditor;
